import { Divider } from '@ngg/components';
import {
  ExceptionalOpeningHoursDay,
  NavigationLinkFragment,
  OpeningHoursDay,
  PageFragment,
  StoreFragment,
} from '@business/gql/graphql';
import { getOpeningHoursText } from '@utils/content/getOpeningHours';
import Link from '@ui/components/Link/Link';
import { Maybe } from 'graphql/jsutils/Maybe';
import useDictionary from '@utils/hooks/useDictionary';
import Paragraph from '@ui/components/Paragraph/Paragraph';

const ContactLinks = ({
  links,
  onClick,
  locale,
}: {
  links?: Array<NavigationLinkFragment | null>;
  onClick?: () => void;
  locale?: string;
}) => {
  if (!links?.length) {
    return null;
  }

  return (
    <div className="space-y-3 pt-4">
      {links.map((link) => (
        <Paragraph key={link?.title} className="text-sm font-normal">
          <Link
            locale={locale}
            href={(link?.internalLink as PageFragment)?.slug || ''}
            onClick={onClick}>
            {link?.title}
          </Link>
        </Paragraph>
      ))}
    </div>
  );
};

const CustomerServiceInfo = ({
  stores,
  locale,
  onClick,
}: {
  stores?: Maybe<StoreFragment>[];
  locale?: string;
  onClick?: () => void;
}) => {
  const dictionary = useDictionary({
    keys: [
      'contactUs',
      'ourStores',
      'openTodayAt',
      'opensTomorrowAt',
      'opensAtDayOfWeekAt',
    ],
  });
  const customerService = stores?.at(0);
  if (!customerService) return null;

  const openingHours = getOpeningHoursText(
    new Date(),
    {
      locale: locale || '',
      openTodayAt: dictionary.openTodayAt,
      opensTomorrowAt: dictionary.opensTomorrowAt,
      opensAtDayOfWeekAt: dictionary.opensAtDayOfWeekAt,
    },
    customerService.openingHoursCollection?.items as OpeningHoursDay[],
    customerService.exceptionalOpeningHoursCollection
      ?.items as ExceptionalOpeningHoursDay[],
  );

  return (
    <div className="flex flex-col pt-4 text-xxs">
      <Paragraph className="font-bold uppercase">
        {dictionary.contactUs}
      </Paragraph>
      {customerService.infoText && (
        <Paragraph className="mt-1">{customerService.infoText}</Paragraph>
      )}
      {customerService.storePageLinkText && customerService.storePage?.slug && (
        <Link
          className="mt-1 place-self-start underline"
          href={customerService.storePage?.slug}
          onClick={onClick}>
          <span>{customerService.storePageLinkText}</span>
        </Link>
      )}
      <Paragraph className="mt-1">{openingHours}</Paragraph>
    </div>
  );
};

const StoreInfo = ({
  stores,
  locale,
  onClick,
}: {
  stores?: Maybe<StoreFragment>[];
  locale?: string;
  onClick?: () => void;
}) => {
  const dictionary = useDictionary({
    keys: ['ourStores', 'openTodayAt', 'opensTomorrowAt', 'opensAtDayOfWeekAt'],
  });
  if (!stores?.length) return null;

  return (
    <div className="mt-4">
      <Paragraph className="mb-1 text-xxs font-bold uppercase">
        {dictionary.ourStores}
      </Paragraph>
      <div className="space-y-6">
        {stores.map((store) => {
          const openingHoursText = getOpeningHoursText(
            new Date(),
            {
              locale: locale || '',
              openTodayAt: dictionary.openTodayAt,
              opensTomorrowAt: dictionary.opensTomorrowAt,
              opensAtDayOfWeekAt: dictionary.opensAtDayOfWeekAt,
            },
            store?.openingHoursCollection?.items as OpeningHoursDay[],
            store?.exceptionalOpeningHoursCollection
              ?.items as ExceptionalOpeningHoursDay[],
          );

          return (
            <div key={store?.sys?.id}>
              <Paragraph className="mt-1 text-xxs">{`${store?.title}, ${store?.address}`}</Paragraph>
              <Paragraph className="mt-1 text-xxs">
                {openingHoursText}
              </Paragraph>
              <Link
                locale={locale}
                className="mt-1 text-xxs underline"
                href={store?.storePage?.slug || ''}
                onClick={onClick}>
                {store?.storePageLinkText}
              </Link>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default function MobileMenuInfo({
  physicalStores,
  onlineStores,
  locale,
  onClick,
  additionalNavigation,
}: {
  physicalStores?: Maybe<StoreFragment>[];
  onlineStores?: Maybe<StoreFragment>[];
  additionalNavigation?: Array<NavigationLinkFragment | null>;
  locale?: string;
  onClick?: () => void;
}) {
  return (
    <div className="bg-beige px-4 pb-6 lg:hidden">
      <Divider className="-mx-4 h-px" />
      <ContactLinks links={additionalNavigation} onClick={onClick} />
      <Divider className="mt-4 h-px" />
      <CustomerServiceInfo
        stores={onlineStores}
        locale={locale}
        onClick={onClick}
      />
      <StoreInfo stores={physicalStores} locale={locale} onClick={onClick} />
    </div>
  );
}
